/**
 * Hook para sincronizar los datos de un cliente desde el padrón AFIP.
 *
 * Flujo:
 *  1. Consulta padrón AFIP via useConsultarCuitAFIP (Netlify function).
 *  2. Persiste en `personas.domicilio_*` + `padron_sincronizado_en`.
 *  3. ORR-233: persiste también categoría de monotributo y actividades en
 *     `cliente_faceta_contable`. El padrón ya las devolvía y se descartaban.
 *  4. Invalida queries relacionadas para refrescar la UI.
 *
 * Defensas:
 *  - assertPermiso('sincronizarPadron'): bloqueo en hook (defense-in-depth).
 *  - personasService.actualizar exige `organizacionId`: filtra `.eq('organizacion_id', orgId)`.
 *  - clientesService.actualizarFacetaContable verifica el cliente contra la org
 *    antes de escribir (la faceta no tiene organizacion_id propio).
 *
 * Devuelve también `previewPadron` que NO persiste (para mostrar diff
 * Antes/Después en AlertDialog antes de confirmar la sincronización).
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth0 } from '@auth0/auth0-react'
import { toast } from 'sonner'
import type { SupabaseClient } from '@supabase/supabase-js'

import { useSupabase } from '@/lib/supabase-context'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'
import { usePermissions } from '@/hooks/usePermissions'
import { useConsultarCuitAFIP } from '@/hooks/useConsultarCuitAFIP'
import { personasService } from '@/services/personasService'
import { clientesService } from '@/services/clientesService'
import { clientesKeys } from '@/hooks/useClientes'
import { notificarCambiosCategoria } from '@/hooks/notificarCambioCategoria'
import { notificacionesService } from '@/core/services/notificacionesService'
import { assertPermiso } from '@/lib/permisos-guard'
import { normalizarCategoriaMonotributo } from '@/lib/monotributo'
import type { Database } from '@/types/database'
import type { PersonaAFIP } from '@/types/afip'

type PersonaUpdate = Database['public']['Tables']['personas']['Update']

interface DomicilioPatch {
  domicilio_calle: string | null
  domicilio_numero: string | null
  domicilio_piso: string | null
  domicilio_depto: string | null
  domicilio_localidad: string | null
  domicilio_provincia: string | null
  domicilio_cp: string | null
  domicilio_pais: string | null
}

/**
 * Mapea el padrón AFIP al patch de personas.domicilio_*.
 *
 * Fuentes:
 *  - calle/numero/piso/depto: `persona.domicilioParsed` (parsing de la dirección raw).
 *  - localidad/provincia/cp:  `persona.domicilio` (campos directos del padrón).
 *  - pais: AFIP no lo devuelve; queda null.
 */
export function padronToDomicilioPatch(persona: PersonaAFIP): DomicilioPatch {
  const parsed = persona.domicilioParsed
  const raw = persona.domicilio
  return {
    domicilio_calle: parsed?.calle ?? null,
    domicilio_numero: parsed?.numero ?? null,
    domicilio_piso: parsed?.piso ?? null,
    domicilio_depto: parsed?.depto ?? null,
    domicilio_localidad: raw?.localidad ?? null,
    domicilio_provincia: raw?.provincia ?? null,
    domicilio_cp: raw?.codigoPostal ?? null,
    domicilio_pais: null,
  }
}

/**
 * ORR-233: mapea el padrón al patch de `cliente_faceta_contable`.
 *
 * A diferencia del domicilio, acá **un campo que ARCA no devuelve se OMITE**, no se
 * escribe como null. El patch es parcial, así que una clave ausente deja el valor
 * cargado intacto. Es la defensa contra el modo de fallo que no dispara error ni
 * retry: un padrón que responde 200 pero incompleto borraría datos buenos.
 *
 * `condicion_iva` queda deliberadamente afuera: pasar un cliente de monotributo a
 * responsable inscripto cambia qué impuestos liquida y qué vencimientos le tocan.
 * El ticket pide categoría y actividades; ese salto merece su propia decisión.
 */
export function padronToFacetaPatch(persona: PersonaAFIP): Record<string, unknown> {
  const patch: Record<string, unknown> = {}

  // AFIP devuelve la categoría con formato variable ("D", "Categoría D", "D - …").
  // La normalización es compartida con el alta: `charAt(0)` sobre "Categoría D"
  // devolvía "C", una categoría válida y equivocada.
  const letra = normalizarCategoriaMonotributo(persona.categoriaMonotributo)
  if (letra) {
    patch.categoria_monotributo = letra
    patch.categoria_origen = 'arca'
  }

  if (persona.actividadPrincipal) {
    patch.actividad_principal = persona.actividadPrincipal
  }
  if (persona.codigoActividadPrincipal) {
    patch.codigo_actividad_afip = persona.codigoActividadPrincipal
  }
  if (persona.actividades && persona.actividades.length > 0) {
    patch.actividades_economicas = persona.actividades.map((a, i) => ({
      codigo: a.codigo,
      descripcion: a.descripcion,
      es_principal: i === 0,
    }))
  }

  return patch
}

/**
 * ORR-351: la categoría de HOY, antes de pisarla. `undefined` = no se pudo leer, y en ese
 * caso no se afirma ningún cambio. Nunca lanza: es para un aviso, no para la escritura.
 */
export const leerCategoriaActual = async (
  client: SupabaseClient<Database>,
  clienteId: string,
): Promise<string | null | undefined> => {
  const { data, error } = await client
    .from('cliente_faceta_contable')
    .select('categoria_monotributo')
    .eq('cliente_id', clienteId)
    .maybeSingle()
  if (error) {
    console.error('No se pudo leer la categoría actual del cliente:', error)
    return undefined
  }
  return data?.categoria_monotributo ?? null
}

/** Pura. Hay cambio solo si el patch trae categoría, se pudo leer la previa y difieren. */
export const decidirCambioCategoria = (
  clienteId: string,
  previa: string | null | undefined,
  patch: Record<string, unknown>,
): { clienteId: string; anterior: string | null; nueva: string } | null => {
  const nueva = patch.categoria_monotributo
  if (typeof nueva !== 'string' || previa === undefined || previa === nueva) return null
  return { clienteId, anterior: previa, nueva }
}

export function useSincronizarPadronCliente(personaId: string, clienteId?: string) {
  const { supabase } = useSupabase()
  const { organizacionId } = useOrganizacion()
  const { puede } = usePermissions()
  const queryClient = useQueryClient()
  const { consultarCuitAsync } = useConsultarCuitAFIP()
  const { user } = useAuth0()

  return useMutation({
    mutationFn: async (cuit: string) => {
      assertPermiso(puede, 'sincronizarPadron')
      if (!supabase) throw new Error('Cliente Supabase no disponible')
      if (!organizacionId) throw new Error('Sin organización activa')
      if (!personaId) throw new Error('personaId requerido')

      const data = await consultarCuitAsync(cuit)
      if (!data.encontrado || !data.persona) {
        throw new Error(
          'No se encontró un CUIT activo en el padrón AFIP. Si la persona solo tiene CUIL (todavía no inscripta), no hay datos para sincronizar.'
        )
      }

      const patch: PersonaUpdate = {
        ...padronToDomicilioPatch(data.persona),
        padron_sincronizado_en: new Date().toISOString(),
      }

      await personasService.actualizar(supabase, personaId, patch, organizacionId)

      // Los campos fiscales viven en otra tabla y son opcionales: sin clienteId
      // el hook sigue sirviendo para sincronizar solo el domicilio.
      const facetaPatch = clienteId ? padronToFacetaPatch(data.persona) : {}

      // ORR-351: leer ANTES de escribir; después ya no hay "antes". Solo si el patch trae
      // categoría: es lo único que dispara el aviso.
      const previa =
        clienteId && typeof facetaPatch.categoria_monotributo === 'string'
          ? await leerCategoriaActual(supabase, clienteId)
          : undefined

      if (clienteId && Object.keys(facetaPatch).length > 0) {
        await clientesService.actualizarFacetaContable(
          supabase,
          clienteId,
          organizacionId,
          facetaPatch
        )
      }

      return {
        camposFiscales: Object.keys(facetaPatch).length,
        cambioCategoria: clienteId ? decidirCambioCategoria(clienteId, previa, facetaPatch) : null,
      }
    },
    onSuccess: async ({ camposFiscales, cambioCategoria }) => {
      queryClient.invalidateQueries({ queryKey: clientesKeys.all })
      toast.success(
        camposFiscales > 0
          ? 'Domicilio y datos fiscales sincronizados desde AFIP'
          : 'Domicilio sincronizado desde AFIP'
      )

      // ORR-351: efecto secundario best-effort. El toast ya salió y la faceta ya está escrita;
      // `obtenerNombreCliente` y `notificarCambiosCategoria` no lanzan.
      if (!cambioCategoria || !supabase) return
      const nombre = await notificacionesService.obtenerNombreCliente(supabase, cambioCategoria.clienteId)
      await notificarCambiosCategoria(supabase, user?.email, [{ ...cambioCategoria, nombre }], user?.name)
    },
    onError: (error: Error) => {
      console.error('Error sincronizando padrón:', error)
      toast.error(`Error sincronizando: ${error.message}`)
    },
  })
}
